// Source: https://github.com/KittyGiraudel/focusable-selectors/blob/efa886e/index.js
// MIT License, Copyright (c) 2021 Kitty Giraudel
const focusableSelector = [
  'a[href]',
  // 'area[href]',
  'input:not([type="hidden"]):not([type="radio"]):not([disabled])',
  'input[type="radio"]:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  // 'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
  '[tabindex]',
].join(',');

const isUntabbable = (element: HTMLElement): boolean => {
  return !element.matches || !!element.matches('[tabindex^="-"], [aria-modal]');
};

export const getFocusTargets = (node: HTMLElement): HTMLElement[] => {
  const nodelist = node.querySelectorAll(focusableSelector);
  const targets = Array.prototype.slice.call(nodelist) as HTMLElement[];
  return targets.filter((target: HTMLElement) => {
    // Filter out elements that have been marked as untabbable
    let parent: HTMLElement | null = target;
    while (parent && parent !== node) {
      if (isUntabbable(parent)) return false;
      parent = parent.parentElement;
    }
    // Filter by visible elements, exclude visually hidden ones
    return (
      target.offsetWidth ||
      target.offsetHeight ||
      target.getClientRects().length
    );
  });
};

export const getNextFocusTarget = (
  node: HTMLElement,
  reverse?: boolean
): HTMLElement | null => {
  const root = document.querySelector('main') || document.body;
  let parent: HTMLElement | null = node;
  while (parent && parent !== root) {
    let sibling: HTMLElement | null = parent;
    while (sibling) {
      sibling = sibling[
        reverse ? 'previousSibling' : 'nextSibling'
      ] as unknown as HTMLElement | null;
      if (!sibling || isUntabbable(sibling)) continue;
      const focusTargets = getFocusTargets(sibling);
      if (focusTargets.length) {
        return focusTargets[reverse ? focusTargets.length - 1 : 0];
      }
    }

    parent = parent.parentElement;
  }

  return null;
};

export const getFirstFocusTarget = (node: HTMLElement): HTMLElement | null =>
  node.querySelector('[autofocus]') ||
  node.querySelector(focusableSelector) ||
  null;

export const isRelatedNode = (
  owner: HTMLElement | null | undefined,
  node: HTMLElement | null | undefined
): boolean => {
  return !!(
    node &&
    owner &&
    (owner === node || owner.contains(node) || node.contains(owner))
  );
};
